//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React from 'react';
import styled from 'styled-components';

import Button from '@components/atoms/button/Button';
import { DefaultButtonStyles } from '@components/atoms/button/buttonStyles';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { isDefined } from '@utils/filterPredicates';

export interface DropdownFilterProps<T extends FilterValue> {
  isDisabled?: boolean;
  placeholder?: string;
  section: FilterSection<T>;
  onChange: (section: FilterSection<T>) => void;
}

export const DropdownFilter = <T extends FilterValue>({
  isDisabled,
  placeholder,
  section,
  onChange,
}: DropdownFilterProps<T>) => {
  const selectedOption = section.options.find((option) => option.isSelected);
  const options = placeholder
    ? [
        {
          label: placeholder,
          value: undefined,
          isSelected: !isDefined(selectedOption), // Show placeholder is selected if none of the other options are selected
        },
        ...section.options,
      ]
    : section.options;

  const handleSelect = (value?: T) => {
    const options = section.options.map((option) => ({
      ...option,
      isSelected: option.value === value,
    }));
    onChange({ ...section, options });
  };

  return (
    <DropdownSection>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <TriggerButton
            buttonStyles={DefaultButtonStyles}
            isDisabled={isDisabled}
            isDropdown={true}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </TriggerButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align='start'>
          {options.map((option) => (
            <DropdownMenu.Item
              key={option.label}
              onSelect={() => handleSelect(option.value)}
            >
              {option.label}
              {option.isSelected && <DropdownMenu.SelectedIcon />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>
    </DropdownSection>
  );
};

const DropdownSection = styled.section(
  ({ theme }) => `
    padding: ${theme.spacings[1]} ${theme.spacings[0]};
  `,
);

const TriggerButton = styled(Button)`
  width: 100%;
`;
